import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'cafe',
    emoji: '☕',
    title: '카페 운영자',
    category: '요식업·경영',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `카페 운영자 지훈씨는 아메리카노와 라떼의 판매량을 연립방정식으로 분석합니다.
하루 총 판매량이 50잔이고 총 매출이 200,000원일 때
아메리카노(3,000원)와 라떼(5,000원)를 각각 몇 잔 팔았는지 구합니다.
"연립방정식으로 두 상품의 판매량을 동시에 찾을 수 있어!"`,
    situation: '아메리카노 x잔, 라떼 y잔. 총 판매량 50잔, 총 매출 200,000원. x와 y를 구하시오.',
    question: 'x + y = 50, 3000x + 5000y = 200000을 연립하여 풀면?',
    xLabel: '변수',
    yLabel: '값',
    equation: 'x + y = 50\n3000x + 5000y = 200000',
    answer: 'x + y = 50 → x = 50 - y\n3000(50-y) + 5000y = 200000\n150000 - 3000y + 5000y = 200000\n2000y = 50000\ny = 25, x = 25\n→ 아메리카노 25잔, 라떼 25잔',
    whyStat: '카페·레스토랑 운영자는 연립방정식으로 메뉴별 판매량·원가·매출을 동시에 분석해 수익성 있는 메뉴 구성을 결정합니다.',
    insight: '스타벅스·이디야·블루보틀 등 카페 프랜차이즈의 운영 분석가는 연립방정식으로 메뉴별 판매 비중과 최적 가격을 산출합니다.',
    realAnswer: 25,
    unit: '잔 (y값)',
    tableData: [
      { x: '아메리카노 (x)', y: 25 },
      { x: '라떼 (y)', y: 25 },
      { x: '합계 (잔)', y: 50 },
      { x: '매출 (원)', y: 200000 },
    ],
    quiz: [
      { q: 'x + y = 10, x - y = 4를 가감법으로 풀면?', a: 'x = 7, y = 3', hint: '두 식을 더하면: 2x = 14 → x = 7. x=7 대입: y = 10-7 = 3' },
      { q: '연립방정식의 해가 존재하려면 두 직선이 어떤 상태여야 하나요?', a: '한 점에서 만나야 한다 (교점이 1개)', hint: '평행하면 해 없음, 일치하면 해 무수히 많음' },
    ],
    jobs: ['카페 운영자', '경영 컨설턴트', '원가 분석가', '프랜차이즈 MD'],
  },
  {
    id: 'traffic',
    emoji: '🚦',
    title: '교통 엔지니어',
    category: '공학·도시',
    color: 'from-sky-400 to-blue-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-700',
    story: `교통 엔지니어 수아씨는 두 도로의 차량 흐름을 연립방정식으로 분석합니다.
A도로와 B도로의 시간당 차량 수 합이 1200대이고,
A도로가 B도로보다 200대 많을 때 각 도로의 차량 수를 구합니다.
"연립방정식으로 복잡한 교통 흐름도 정확히 분석해!"`,
    situation: 'A도로 x대, B도로 y대. x + y = 1200, x - y = 200. 각각 몇 대인지 구하시오.',
    question: 'x + y = 1200, x - y = 200을 가감법으로 풀면?',
    xLabel: '변수',
    yLabel: '값 (대)',
    equation: 'x + y = 1200\nx - y = 200',
    answer: '두 식을 더하면:\n(x+y) + (x-y) = 1200 + 200\n2x = 1400\nx = 700\ny = 1200 - 700 = 500\n→ A도로 700대, B도로 500대',
    whyStat: '교통 엔지니어는 연립방정식으로 도로별 교통량·신호 주기·병목 지점을 분석해 교통 흐름을 최적화합니다.',
    insight: '국토교통부·서울시·교통연구원의 엔지니어는 연립방정식으로 교통량 데이터를 분석해 신호 시스템과 도로 설계를 개선합니다.',
    realAnswer: 700,
    unit: '대 (A도로)',
    tableData: [
      { x: 'A도로 (x)', y: 700 },
      { x: 'B도로 (y)', y: 500 },
      { x: '합계 (대)', y: 1200 },
      { x: '차이 (대)', y: 200 },
    ],
    quiz: [
      { q: '2x + y = 7, x + y = 4를 가감법으로 풀면?', a: 'x = 3, y = 1', hint: '첫 번째 식에서 두 번째 식 빼기: x = 3. 대입하면 y = 4-3 = 1' },
      { q: '가감법의 원리는?', a: '두 방정식을 더하거나 빼서 미지수를 하나 없애는 방법', hint: '같은 계수의 항을 더하거나 빼 한 변수를 소거' },
    ],
    jobs: ['교통 엔지니어', '도시 계획가', 'GIS 분석가', '스마트시티 개발자'],
  },
  {
    id: 'finance',
    emoji: '💰',
    title: '재무 분석가',
    category: '금융·경제',
    color: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `재무 분석가 민준씨는 두 펀드의 투자 배분을 연립방정식으로 계산합니다.
총 투자금 1000만원을 A펀드(연 5%)와 B펀드(연 3%)에 나눠 투자해
연간 수익 40만원을 목표로 하는 최적 배분을 구합니다.
"연립방정식으로 투자 포트폴리오를 정밀하게 설계해!"`,
    situation: 'A펀드 x만원, B펀드 y만원. x + y = 1000, 0.05x + 0.03y = 40. x, y를 구하시오.',
    question: 'x + y = 1000, 5x + 3y = 4000(×100)을 대입법으로 풀면?',
    xLabel: '변수',
    yLabel: '값 (만원)',
    equation: 'x + y = 1000\n0.05x + 0.03y = 40',
    answer: 'x = 1000 - y 대입:\n0.05(1000-y) + 0.03y = 40\n50 - 0.05y + 0.03y = 40\n-0.02y = -10\ny = 500, x = 500\n→ A펀드 500만원, B펀드 500만원',
    whyStat: '재무 분석가는 연립방정식으로 투자 포트폴리오 배분·수익률·리스크를 동시에 고려한 최적 투자 전략을 수립합니다.',
    insight: '증권사·자산운용사·은행의 재무 분석가는 연립방정식으로 포트폴리오 최적화 문제를 풀어 고객 맞춤 투자안을 제시합니다.',
    realAnswer: 500,
    unit: '만원 (y값)',
    tableData: [
      { x: 'A펀드 x (만원)', y: 500 },
      { x: 'B펀드 y (만원)', y: 500 },
      { x: '합계 (만원)', y: 1000 },
      { x: '수익 (만원)', y: 40 },
    ],
    quiz: [
      { q: 'x + y = 5, 2x - y = 1을 대입법으로 풀면?', a: 'x = 2, y = 3', hint: 'y = 5 - x를 두 번째 식에 대입: 2x-(5-x)=1 → 3x=6 → x=2, y=3' },
      { q: '대입법의 원리는?', a: '한 방정식에서 한 변수를 다른 변수로 나타낸 뒤 나머지 방정식에 대입하는 방법', hint: '예: y = 3 - x를 다른 식에 대입' },
    ],
    jobs: ['재무 분석가', '포트폴리오 매니저', '핀테크 개발자', '보험 계리사'],
  },
  {
    id: 'chemistry',
    emoji: '🧪',
    title: '화학 연구원',
    category: '과학·연구',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `화학 연구원 예은씨는 두 농도 용액의 혼합 비율을 연립방정식으로 계산합니다.
10% 식염수와 4% 식염수를 합쳐 8% 식염수 300 g을 만들 때
각각 몇 g씩 섞어야 하는지 구합니다.
"연립방정식으로 혼합 비율을 정확히 계산해!"`,
    situation: '10% 식염수 x g, 4% 식염수 y g. x + y = 300, 0.1x + 0.04y = 0.08×300 = 24. x, y를 구하시오.',
    question: 'x + y = 300, 0.1x + 0.04y = 24를 풀면?',
    xLabel: '변수',
    yLabel: '값 (g)',
    equation: 'x + y = 300\n0.1x + 0.04y = 24',
    answer: 'x = 300 - y 대입:\n0.1(300-y) + 0.04y = 24\n30 - 0.1y + 0.04y = 24\n-0.06y = -6\ny = 100, x = 200\n→ 10% 식염수 200 g, 4% 식염수 100 g',
    whyStat: '화학 연구원은 연립방정식으로 혼합 용액의 농도·비율·성분량을 계산해 실험 조건을 정밀하게 설계합니다.',
    insight: 'LG화학·삼성SDI·한화 등 화학·제약 기업의 연구원은 연립방정식으로 원료 혼합 비율을 최적화해 신소재와 의약품을 개발합니다.',
    realAnswer: 200,
    unit: 'g (x값, 10%)',
    tableData: [
      { x: '10% 식염수 x (g)', y: 200 },
      { x: '4% 식염수 y (g)', y: 100 },
      { x: '합계 (g)', y: 300 },
      { x: '소금 함량 (g)', y: 24 },
    ],
    quiz: [
      { q: '2x + 3y = 12, x = 3을 대입하면 y는?', a: 'y = 2', hint: '2×3 + 3y = 12 → 6 + 3y = 12 → 3y = 6 → y = 2' },
      { q: '농도 문제에서 두 식이 의미하는 것은?', a: '①용액의 양(g) 합산식, ②소금의 양(g) 합산식', hint: '양: x+y=전체. 소금: 농도1×x + 농도2×y = 농도목표×전체' },
    ],
    jobs: ['화학 연구원', '제약 연구원', '식품 과학자', '환경 분석가'],
  },
  {
    id: 'smartfarm',
    emoji: '🌾',
    title: '스마트팜 경영자',
    category: '농업·환경',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `스마트팜 경영자 준혁씨는 작물 재배 면적을 연립방정식으로 계획합니다.
딸기와 토마토 재배 면적 합이 500㎡이고
딸기 수익(㎡당 2만원)과 토마토 수익(㎡당 1만5천원) 합이 850만원일 때
각 작물의 면적을 구합니다.
"연립방정식으로 최적 작물 배분을 설계해!"`,
    situation: '딸기 x㎡, 토마토 y㎡. x + y = 500, 2x + 1.5y = 850. x, y를 구하시오.',
    question: 'x + y = 500, 2x + 1.5y = 850을 풀면?',
    xLabel: '변수',
    yLabel: '값 (㎡)',
    equation: 'x + y = 500\n2x + 1.5y = 850',
    answer: 'x = 500 - y 대입:\n2(500-y) + 1.5y = 850\n1000 - 2y + 1.5y = 850\n-0.5y = -150\ny = 300, x = 200\n→ 딸기 200㎡, 토마토 300㎡',
    whyStat: '스마트팜 경영자는 연립방정식으로 작물별 면적·수익·비용을 동시에 최적화해 농장 수익성을 극대화합니다.',
    insight: '농촌진흥청·스마트팜 스타트업·대형 농업 기업의 경영자는 연립방정식으로 작물 배분·자원 최적화를 계획해 스마트 농업을 실현합니다.',
    realAnswer: 200,
    unit: '㎡ (x값, 딸기)',
    tableData: [
      { x: '딸기 x (㎡)', y: 200 },
      { x: '토마토 y (㎡)', y: 300 },
      { x: '합계 (㎡)', y: 500 },
      { x: '수익 (만원)', y: 850 },
    ],
    quiz: [
      { q: '3x + 2y = 16, x + y = 6을 가감법으로 풀면?', a: 'x = 4, y = 2', hint: '두 번째 식 ×2: 2x+2y=12. 첫째-이것: x=4. 대입: y=2' },
      { q: '연립방정식 풀이에서 가감법과 대입법 중 언제 가감법이 더 편할까?', a: '두 식의 특정 항의 계수가 같거나 반대일 때 (바로 더하거나 빼서 소거)', hint: 'x+y=5, x-y=1 → 더하면 2x=6' },
    ],
    jobs: ['스마트팜 경영자', '농업 데이터 분석가', '작물 재배 전문가', '애그테크 개발자'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '☕',
    title: '경영·원가 분석가',
    example: '메뉴별 판매량·원가·매출을 연립방정식으로 동시에 분석',
    formula: 'x + y = 총량, px + qy = 총매출',
    salary: '연 4,500만~1억원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '🚦',
    title: '교통·도시 공학자',
    example: '도로별 교통량·신호 최적화를 연립방정식으로 설계',
    formula: 'x + y = N, x - y = d',
    salary: '연 4,500만~1억원',
    color: 'from-sky-500 to-blue-500',
  },
  {
    icon: '💰',
    title: '재무·투자 분석가',
    example: '포트폴리오 배분·수익률을 연립방정식으로 최적화',
    formula: 'x + y = M, r₁x + r₂y = 목표수익',
    salary: '연 5,000만~1억 5,000만원',
    color: 'from-emerald-500 to-green-500',
  },
  {
    icon: '🧪',
    title: '화학·제약 연구원',
    example: '혼합 용액 농도·비율을 연립방정식으로 정밀 계산',
    formula: 'x + y = W, c₁x + c₂y = cW',
    salary: '연 4,500만~1억원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '🌾',
    title: '스마트팜·농업 경영자',
    example: '작물별 면적·수익을 연립방정식으로 최적 배분',
    formula: 'x + y = A, p₁x + p₂y = 목표수익',
    salary: '연 4,000만~9,000만원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '🏗️',
    title: '건축·구조 공학자',
    example: '재료별 사용량·하중을 연립방정식으로 계산해 건물 설계',
    formula: 'ax + by = F₁, cx + dy = F₂',
    salary: '연 4,500만~1억원',
    color: 'from-indigo-500 to-violet-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '📋',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '가감법으로 풀어보세요.\nx + y = 8\nx - y = 2',
    hint: '두 식을 더하면: 2x = 10 → x = 5. 대입하면 y = 3',
    answer: 'x = 5, y = 3',
    explanation: '① x + y = 8\n② x - y = 2\n①+②: 2x = 10 → x = 5\n x=5를 ①에 대입: 5 + y = 8 → y = 3\n해: x = 5, y = 3',
  },
  {
    id: 'c2',
    emoji: '🌿',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '대입법으로 풀어보세요.\n2x + y = 9\nx = y + 3',
    hint: 'x = y + 3을 첫 번째 식에 대입: 2(y+3) + y = 9',
    answer: 'x = 5, y = 2',
    explanation: 'x = y + 3을 ①에 대입:\n2(y+3) + y = 9\n2y + 6 + y = 9\n3y = 3\ny = 1\n\n※ 정확 계산:\n3y = 9 - 6 = 3 → y = 1\nx = 1 + 3 = 4\n\n정확한 해: x = 4, y = 1',
  },
  {
    id: 'c3',
    emoji: '📈',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '활용 문제: 어른 입장료 8,000원, 어린이 입장료 5,000원. 총 20명이 입장해 총 요금 130,000원일 때 어른과 어린이 수를 구하시오.',
    hint: '어른 x명, 어린이 y명. x+y=20, 8000x+5000y=130000',
    answer: '어른 10명, 어린이 10명',
    explanation: 'x + y = 20  →  y = 20 - x\n8000x + 5000(20-x) = 130000\n8000x + 100000 - 5000x = 130000\n3000x = 30000\nx = 10, y = 10\n→ 어른 10명, 어린이 10명',
  },
  {
    id: 'c4',
    emoji: '🔬',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '계수 조정 후 가감법으로 풀어보세요.\n2x + 3y = 16\n3x + 2y = 14',
    hint: '①×3 - ②×2 또는 ①×2 - ②×3으로 한 변수를 소거. y 소거: ①×2-②×3',
    answer: 'x = 2, y = 4',
    explanation: '①×2: 4x + 6y = 32\n②×3: 9x + 6y = 42\n②×3 - ①×2: 5x = 10 → x = 2\nx=2를 ①에 대입: 4 + 3y = 16 → 3y = 12 → y = 4\n해: x = 2, y = 4\n검산: 2×2+3×4=4+12=16 ✅\n3×2+2×4=6+8=14 ✅',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '요식업·경영': 'bg-amber-100 text-amber-700',
  '공학·도시': 'bg-sky-100 text-sky-700',
  '금융·경제': 'bg-emerald-100 text-emerald-700',
  '과학·연구': 'bg-violet-100 text-violet-700',
  '농업·환경': 'bg-rose-100 text-rose-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'elim' | 'subst'

interface CalcResult {
  mode: CalcMode
  steps: string[]
  x: number
  y: number
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('elim')
  // 가감법: a1x + b1y = c1, a2x + b2y = c2
  const [a1, setA1] = useState('')
  const [b1, setB1] = useState('')
  const [c1, setC1] = useState('')
  const [a2, setA2] = useState('')
  const [b2, setB2] = useState('')
  const [c2, setC2] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => {
    setA1(''); setB1(''); setC1(''); setA2(''); setB2(''); setC2('')
    setResult(null); setUserAnswer(''); setChecked(null)
  }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const na1 = parseFloat(a1), nb1 = parseFloat(b1), nc1 = parseFloat(c1)
    const na2 = parseFloat(a2), nb2 = parseFloat(b2), nc2 = parseFloat(c2)
    if ([na1, nb1, nc1, na2, nb2, nc2].some(isNaN)) return

    const det = na1 * nb2 - na2 * nb1
    if (det === 0) return
    const x = (nc1 * nb2 - nc2 * nb1) / det
    const y = (na1 * nc2 - na2 * nc1) / det

    if (mode === 'elim') {
      // 가감법: y 소거 (×nb2, ×nb1)
      const m1 = Math.abs(nb2), m2 = Math.abs(nb1)
      const sign = nb2 * nb1 > 0 ? '빼기' : '더하기'
      setResult({
        mode,
        steps: [
          `식①: ${na1}x + ${nb1}y = ${nc1}`,
          `식②: ${na2}x + ${nb2}y = ${nc2}`,
          `①×${m2}: ${na1 * m2}x + ${nb1 * m2}y = ${nc1 * m2}`,
          `②×${m1}: ${na2 * m1}x + ${nb2 * m1}y = ${nc2 * m1}`,
          `y 소거 (${sign}): ${na1 * m2 - na2 * m1}x = ${nc1 * m2 - nc2 * m1}`,
          `x = ${x}`,
          `x=${x}를 ①에 대입: y = ${y}`,
        ],
        x,
        y,
      })
    } else {
      // 대입법: ①에서 x = (c1 - b1*y) / a1
      setResult({
        mode,
        steps: [
          `식①: ${na1}x + ${nb1}y = ${nc1}`,
          `식②: ${na2}x + ${nb2}y = ${nc2}`,
          `①에서 x = (${nc1} - ${nb1}y) ÷ ${na1}`,
          `②에 대입: ${na2}×(${nc1}-${nb1}y)÷${na1} + ${nb2}y = ${nc2}`,
          `y 정리: y = ${y}`,
          `x = (${nc1} - ${nb1}×${y}) ÷ ${na1} = ${x}`,
        ],
        x,
        y,
      })
    }
    setUserAnswer(''); setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.x) * 0.02 + 0.001
    setChecked(Math.abs(ua - result.x) <= tolerance)
  }

  return (
    <div className="card border-2 border-violet-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-violet-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('elim')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'elim' ? 'bg-violet-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            가감법
          </button>
          <button
            onClick={() => handleModeChange('subst')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'subst' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            대입법
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-violet-700 text-sm">
          {mode === 'elim'
            ? 'a₁x + b₁y = c₁ / a₂x + b₂y = c₂  →  가감법으로 소거'
            : 'a₁x + b₁y = c₁ / a₂x + b₂y = c₂  →  대입법으로 풀기'}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-xs font-semibold text-slate-500">🔢 a₁ (식①의 x계수)</label>
          <input type="number" value={a1} onChange={e => { setA1(e.target.value); setResult(null) }}
            placeholder="예: 1"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">🔢 b₁ (식①의 y계수)</label>
          <input type="number" value={b1} onChange={e => { setB1(e.target.value); setResult(null) }}
            placeholder="예: 1"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">🔢 c₁ (식①의 우변)</label>
          <input type="number" value={c1} onChange={e => { setC1(e.target.value); setResult(null) }}
            placeholder="예: 50"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-300 transition-all" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">🔢 a₂ (식②의 x계수)</label>
          <input type="number" value={a2} onChange={e => { setA2(e.target.value); setResult(null) }}
            placeholder="예: 3000"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition-all" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">🔢 b₂ (식②의 y계수)</label>
          <input type="number" value={b2} onChange={e => { setB2(e.target.value); setResult(null) }}
            placeholder="예: 5000"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition-all" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">🔢 c₂ (식②의 우변)</label>
          <input type="number" value={c2} onChange={e => { setC2(e.target.value); setResult(null) }}
            placeholder="예: 200000"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 transition-all" />
        </div>
      </div>
      <button
        onClick={calculate}
        disabled={!a1 || !b1 || !c1 || !a2 || !b2 || !c2}
        className={`w-full py-2.5 rounded-xl font-bold text-sm transition-all mb-4 ${
          mode === 'elim'
            ? 'bg-violet-600 hover:bg-violet-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
        }`}
      >
        계산!
      </button>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3">📝 풀이 과정 (단계별)</p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-violet-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-violet-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'elim' ? 'bg-violet-50 border-violet-300' : 'bg-indigo-50 border-indigo-300'
          }`}>
            <div className="flex items-baseline gap-4">
              <span className={`text-2xl font-black ${mode === 'elim' ? 'text-violet-700' : 'text-indigo-700'}`}>
                x = {result.x}
              </span>
              <span className={`text-2xl font-black ${mode === 'elim' ? 'text-violet-700' : 'text-indigo-700'}`}>
                y = {result.y}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 x값 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder="x 값을 입력하세요"
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-violet-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3 whitespace-pre-line">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-violet-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-violet-600 leading-relaxed whitespace-pre-line">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function A2_3RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 연립방정식', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 연립일차방정식</h1>
            <p className="text-sm text-slate-500">카페 운영자, 교통 엔지니어, 재무 분석가, 화학 연구원, 스마트팜 — 모두 연립방정식을 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 연립방정식을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">가감법·대입법으로 두 미지수를 동시에 구하는 능력이 복잡한 실생활 문제의 핵심입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '☕', job: '카페 운영자' },
              { icon: '🚦', job: '교통 엔지니어' },
              { icon: '💰', job: '재무 분석가' },
              { icon: '🧪', job: '화학 연구원' },
              { icon: '🌾', job: '스마트팜 경영자' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-violet-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-violet-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌측: 시나리오 목록 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-violet-600 font-semibold whitespace-pre-line">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* 우측: 시나리오 상세 */}
            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 연립방정식</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 연립방정식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-base font-black text-violet-700 font-mono whitespace-pre-line">{selected.equation}</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-violet-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 연립방정식인가?</span> {selected.whyStat}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-violet-500" /> 해 분석표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-violet-50 text-violet-700 text-left">{selected.yLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-violet-700">{row.y}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 해 분석표를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-violet-500" /> 이 상황에서 연립방정식을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['미지수 2개', '연립방정식', '가감법', '대입법', '해의 검산', '활용 문제'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 연립일차방정식 핵심 개념 정리</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🔗', title: '연립방정식의 개념', desc: '두 미지수 x, y를 포함한\n일차방정식 두 개를 묶은 것\n해: 두 방정식을 모두 만족하는 (x, y)\n두 직선의 교점이 해', color: 'text-violet-600' },
                { icon: '➕', title: '가감법', desc: '두 방정식을 더하거나 빼서\n한 미지수를 소거하는 방법\n계수가 같지 않으면 배수로 맞춤\n① 계수 맞추기 → ② 더하기/빼기', color: 'text-indigo-600' },
                { icon: '🔄', title: '대입법', desc: '한 방정식에서 한 변수를\n다른 변수로 나타낸 뒤 대입\n계수가 1인 항이 있을 때 편리\n① 표현 → ② 대입 → ③ 풀기', color: 'text-purple-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 연립방정식을 사용하는 직업들</h2>
            <p className="text-sm text-violet-100">
              두 미지수·연립방정식·가감법·대입법은 경영·공학·금융·과학·농업 등 수많은 직업의 핵심 분석 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-violet-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 연립방정식이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '선형 대수·AI 모델 학습', desc: '신경망의 가중치 업데이트, 행렬 방정식 풀이 등 AI의 핵심 연산이 연립방정식의 확장입니다.', color: 'border-yellow-400' },
                { title: '최적화·공급망 관리', desc: '물류 비용·생산량·재고량을 동시에 최적화하는 선형 계획법이 연립방정식을 기반으로 합니다.', color: 'border-violet-400' },
                { title: '컴퓨터 그래픽·게임', desc: '3D 공간의 교차점 계산, 물리 엔진의 충돌 처리에 연립방정식이 활용됩니다.', color: 'border-emerald-400' },
                { title: '회로·통신 설계', desc: '전기 회로의 전류·전압 분석(키르히호프 법칙)이 연립방정식으로 표현됩니다.', color: 'border-sky-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-violet-200 bg-violet-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-violet-600" /> 연립방정식에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '일차방정식', active: false },
                { step: '중2', topic: '연립방정식', active: true },
                { step: '중3', topic: '이차방정식', active: false },
                { step: '고1', topic: '연립부등식', active: false },
                { step: '고2', topic: '행렬방정식', active: false },
                { step: '대학', topic: '선형대수학', active: false },
                { step: '직업', topic: 'AI·최적화', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-violet-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-violet-500 to-purple-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-violet-100">
              실제 상황에서 연립일차방정식을 직접 풀어보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-violet-500" /> 연립방정식 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 연립방정식 세우기', desc: '미지수 2개 설정 (x, y)\n조건 1 → 방정식 ①\n조건 2 → 방정식 ②\n두 식을 묶어 연립방정식 구성', example: '예: x+y=10, 3x+5y=40', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                { step: '② 풀이 (가감법)', desc: '① 한 변수의 계수를 같게 맞춤\n② 두 식 더하거나 빼서 소거\n③ 남은 변수 풀기\n④ 대입해 나머지 변수 구하기', example: '예: 2x=10 → x=5 → y=3', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
                { step: '③ 풀이 (대입법)', desc: '① 한 식에서 한 변수 표현\n② 나머지 식에 대입\n③ 일차방정식 풀기\n④ 검산: 두 식에 모두 대입', example: '예: y=3-x 대입 → x=1, y=2', color: 'bg-purple-50 border-purple-200 text-purple-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-violet-700">미지수 설정 → 연립방정식 세우기 → 가감법/대입법 → 검산!</p>
              <p className="text-xs text-violet-500 mt-1">연립방정식의 흐름을 이해하면 두 조건이 있는 모든 문제를 풀 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
